import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Box, Typography, Stack, Chip } from '@mui/material';
import { friendlyNameConverter } from '../../../../services/localizationService';
import { extractValues, pearsonCorrelation, spearmanCorrelation } from '../statistics';
import { useTimeline } from '../TimelineContext';

export default function CorrelationWidget({ recordings = [], widgetConfig }) {
  const { window: timeWindow } = useTimeline();
  const recId = widgetConfig?.recId || '';
  const sensorKeyX = widgetConfig?.sensorKeyX || '';
  const sensorKeyY = widgetConfig?.sensorKeyY || '';

  const rec = recordings.find((r) => r.id === recId);

  const { scatterData, pearson, spearman } = useMemo(() => {
    if (!rec || !sensorKeyX || !sensorKeyY) return { scatterData: null, pearson: null, spearman: null };
    const xs = extractValues(rec, sensorKeyX, timeWindow?.start, timeWindow?.end);
    const ys = extractValues(rec, sensorKeyY, timeWindow?.start, timeWindow?.end);
    const n = Math.min(xs.length, ys.length);
    const data = Array.from({ length: n }, (_, i) => [xs[i], ys[i]]);
    return {
      scatterData: data,
      pearson: pearsonCorrelation(xs, ys),
      spearman: spearmanCorrelation(xs, ys),
    };
  }, [rec, sensorKeyX, sensorKeyY, timeWindow]);

  const nameX = friendlyNameConverter(sensorKeyX || '');
  const nameY = friendlyNameConverter(sensorKeyY || '');

  const chartOptions = {
    chart: { type: 'scatter', toolbar: { show: false }, zoom: { enabled: true }, animations: { enabled: false } },
    xaxis: { title: { text: nameX }, tickAmount: 5 },
    yaxis: { title: { text: nameY }, tickAmount: 5 },
    markers: { size: 3, opacity: 0.6 },
    tooltip: { custom: ({ dataPointIndex }) => {
      if (!scatterData?.[dataPointIndex]) return '';
      const [x, y] = scatterData[dataPointIndex];
      return `<div style="padding:6px">${nameX}: <b>${x.toFixed(2)}</b><br/>${nameY}: <b>${y.toFixed(2)}</b></div>`;
    }},
    grid: { padding: { left: 10, right: 10 } },
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {scatterData && pearson ? (
        <Box sx={{ flex: 1, overflow: 'hidden', px: 1 }}>
          <Stack direction="row" spacing={0.5} sx={{ mb: 0.5 }}>
            <Chip label={`Pearson r=${pearson.r.toFixed(3)} (${pearson.interpretation})`} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
            <Chip label={`Spearman ρ=${spearman.rho.toFixed(3)}`} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
            {timeWindow && <Chip label="Windowed" size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />}
          </Stack>
          <ReactApexChart type="scatter" series={[{ name: 'Data', data: scatterData }]} options={chartOptions} height="90%" />
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">Select a recording and two sensors</Typography>
        </Box>
      )}
    </Box>
  );
}
